import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormArray } from '@angular/forms';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperPrevious,
} from '@angular/material/stepper';
import { MatButton } from '@angular/material/button';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { ResponsiveService } from '../../../../services/responsive.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { MatVerticalStepperScrollerDirective } from '../../../../directives/mat-vertical-stepper-scroller.directive';
import { UsersService } from '../../../../services/users.service';
import { ConfigService } from '../../../services/config.service';
import { ToastService } from '../../../../services/toast.service';
import { IFormProgress } from '../../../../interfaces/IFormProgress.interface';

@Component({
  selector: 'app-formly-field-stepper',
  templateUrl: './stepper-form.component.html',
  styleUrl: './stepper-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatStepper,
    MatStep,
    FormlyModule,
    MatStepLabel,
    MatStepperPrevious,
    MatButton,
    TranslatePipe,
    NgxSkeletonLoaderComponent,
    MatVerticalStepperScrollerDirective,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FormlyFieldStepper extends FieldType implements OnInit, OnDestroy {
  protected readonly stepper = viewChild<MatStepper>('formlyStepper');

  protected overViewFields: FormlyFieldConfig[] = [];
  protected isLastStep = signal(false);
  protected formVersion = signal(0);
  protected hasChanges = false;

  private readonly _usersService = inject(UsersService);
  private readonly _configService = inject(ConfigService).formlyVariablesConfig;
  private readonly _toastService = inject(ToastService);
  private readonly _translateService = inject(TranslateService);
  private readonly _responsiveService = inject(ResponsiveService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private initialModel: Record<string, unknown> = {};
  private progressInfo: IFormProgress | null = null;
  protected stepperOrientation = computed(() =>
    this._responsiveService.isMobile() ? 'vertical' : 'horizontal',
  );

  protected visibleSteps = computed(() => {
    // Read formVersion to force re-computation on form changes
    this.formVersion();
    return this.field.fieldGroup?.filter((step) => !step.hide) ?? [];
  });

  constructor() {
    super();

    effect(() => {
      if (this.isLastStep()) {
        this.generateOverview();
      } else {
        this.overViewFields = [];
      }
    });
  }

  async ngOnInit() {
    if (this.isAutoSaveEnabled()) {
      await this.loadSavedProgress();
      this.setupAutoSave();
    }

    if (this.form) {
      this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.formVersion.update((v) => v + 1);
        this._cdr.markForCheck();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isAutoSaveEnabled(): boolean {
    return this.field.props?.['autoSave'] === true;
  }

  private setupAutoSave(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(this._configService.formChangeCheck),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.hasChanges = this.hasFormChanged();
      });

    setInterval(async () => {
      if (this.hasChanges) {
        await this.saveProgress(true);
      }
    }, this._configService.autoSave);
  }

  private hasFormChanged(): boolean {
    return JSON.stringify(this.model) !== JSON.stringify(this.initialModel);
  }

  private async saveProgress(silent = false): Promise<void> {
    if (!this.field.id) return;

    this.progressInfo = {
      survey_Id: Number(this.field.id),
      answer_obj: this.model,
    };

    await this._usersService.saveSurvey(this.progressInfo);

    if (!silent) {
      this._toastService.showNotification(
        this._translateService.instant('formly.saveProgress'),
        this._configService.snackBar,
      );
    }

    this.hasChanges = false;
  }

  protected onSaveProgress(): void {
    this.saveProgress().then();
  }

  private async loadSavedProgress(): Promise<void> {
    const formId = Number(this.field.id);
    if (!formId) return;

    try {
      this._toastService.showNotification(
        this._translateService.instant('formly.progressLoading'),
        this._configService.snackBar,
      );

      const savedProgress = await this._usersService.getSurveyProgress(formId);
      if (savedProgress) {
        this.form.patchValue(savedProgress.answer_obj);
      }
    } catch (error) {
      console.error('Error loading saved progress:', error);
    }
  }

  isValid(field: FormlyFieldConfig): boolean {
    if (field.hide) {
      return true;
    }

    // Container groups/steps without a 'key' inherit the root formControl.
    // Only inspect control validity directly if the field has its own 'key'.
    if (field.key && field.formControl) {
      const control = field.formControl;

      if (!control.disabled) {
        if (control.invalid) {
          return false;
        }

        const isArrayControl =
          control instanceof FormArray || Array.isArray(control.value);

        if (isArrayControl) {
          const minItems =
            (field.props?.['minItems'] as number | undefined) ??
            (field.props?.required ? 1 : 0);
          const length =
            control instanceof FormArray
              ? control.length
              : (control.value?.length ?? 0);

          if (length < minItems) {
            return false;
          }

          if (control instanceof FormArray) {
            const hasInvalidChild = control.controls.some(
              (childControl) => childControl.invalid,
            );
            if (hasInvalidChild) {
              return false;
            }
          }
        } else if (field.props?.required) {
          const val = control.value;
          if (
            val === null ||
            val === undefined ||
            val === '' ||
            (typeof val === 'string' && val.trim() === '')
          ) {
            return false;
          }
        }
      }
    }

    if (field.fieldGroup) {
      return field.fieldGroup.every((f) => this.isValid(f));
    }

    return true;
  }

  private getInvalidControls(field: FormlyFieldConfig): string[] {
    if (field.hide) {
      return [];
    }

    const invalid: string[] = [];

    if (field.key && field.formControl) {
      const control = field.formControl;

      if (!control.disabled) {
        if (control.invalid) {
          const errorMsg = control.errors
            ? JSON.stringify(control.errors)
            : 'Child controls invalid';
          invalid.push(`${String(field.key)} (Errors: ${errorMsg})`);
        }

        const isArrayControl =
          control instanceof FormArray || Array.isArray(control.value);

        if (isArrayControl) {
          const minItems =
            (field.props?.['minItems'] as number | undefined) ??
            (field.props?.required ? 1 : 0);
          const length =
            control instanceof FormArray
              ? control.length
              : (control.value?.length ?? 0);

          if (length < minItems) {
            invalid.push(
              `${String(field.key)} (Array requires at least ${minItems} item(s), has ${length})`,
            );
          }
        } else if (field.props?.required) {
          const val = control.value;
          if (
            val === null ||
            val === undefined ||
            val === '' ||
            (typeof val === 'string' && val.trim() === '')
          ) {
            if (!control.invalid) {
              invalid.push(`${String(field.key)} (Required field is empty)`);
            }
          }
        }
      }
    }

    if (field.fieldGroup) {
      field.fieldGroup.forEach((f) => {
        invalid.push(...this.getInvalidControls(f));
      });
    }

    return invalid;
  }

  private createOverview(field: FormlyFieldConfig): FormlyFieldConfig {
    return {
      ...field,
      id: `${field.id}_overview`,
      key: field.key,
      props: {
        ...field.props,
        disabled: true,
      },
      hide: field.props?.['dontIncludeInOverview'] ? true : field.hide,
      fieldGroup: field.fieldGroup?.map((f) => this.createOverview(f)),
    };
  }

  private generateOverview(): void {
    if (!this.field.fieldGroup) return;

    this.overViewFields = this.field.fieldGroup.map((step) =>
      this.createOverview(step),
    );
  }

  onStepChange(event: StepperSelectionEvent): void {
    const stepperInstance = this.stepper();
    if (!stepperInstance) return;

    const isLast = stepperInstance.steps.length - 1 === event.selectedIndex;
    this.isLastStep.set(isLast);
  }

  handleNextClick(step: FormlyFieldConfig, next: boolean): void {
    this.markFieldsAsTouched(step);

    const stepValid = this.isValid(step);

    if (!stepValid) {
      console.log('Ungültige Controls:', this.getInvalidControls(step));
      this._cdr.markForCheck();
      return;
    }

    if (next) {
      this.stepper()?.next();
    }
  }

  private markFieldsAsTouched(field: FormlyFieldConfig): void {
    if (field.hide) {
      return;
    }

    if (field.key && field.formControl) {
      field.formControl.markAsTouched();
      field.formControl.markAsDirty();
      if ('markAllAsTouched' in field.formControl) {
        (field.formControl as FormArray).markAllAsTouched();
      }
      field.formControl.updateValueAndValidity({ onlySelf: true });
    }

    if (field.fieldGroup) {
      field.fieldGroup.forEach((f) => this.markFieldsAsTouched(f));
    }
  }
}
